import {
    Block,
    BreachEvent,
    CardEvents,
    CollectionEvent,
    LinkClickedEvent,
    PlayerEvent,
    PublicationType,
    RequiredAccess,
    useFeature,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import { sport } from '@west-australian-newspapers/test-msg-builder/dist/cjs/data/test-topics'
import H from 'history'
import React from 'react'
import { Logger } from 'typescript-log'
import { useFeatureForSection } from '../../../../feature-toggling'
import {
    BASE_50_DEAL_OFFERCODE,
    useUpdateBreachWallContext,
} from '../../common'
import { showBreachDiscount, showBreachForTopic } from '../../helpers'
import { ArticlePreview } from './ArticlePreview/ArticlePreview'
import { BlackFridayBreachNotification } from './BlackFridayBreachNotification/BlackFridayBreachNotification'
import { BreachNotification } from './BreachNotification/BreachNotification'
import { SportBreachNotification } from './SportBreachNotification/SportBreachNotification'

export interface BreachNotificationWrapperProps {
    publication: PublicationType
    breachDeal: string
    breachCreativeName: string
    requiredAccess: RequiredAccess
    onEvent: (
        event:
            | BreachEvent
            | PlayerEvent
            | LinkClickedEvent
            | CardEvents
            | CollectionEvent,
    ) => void
    type: BreachWallContentType
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
    blocks: Block[]
    log: Logger
}

export type BreachWallContentType =
    | 'default'
    | 'sport'
    | 'black-friday'
    | 'top-50-schools'
    | '30-off-breach-discount-biked130' // used on specific article categories
    | '50-off-breach-discount-govan50' // used on specific article categories
    | '50-off-breach-discount-coviddig'
    | '50-off-afl-breach-screen'
    | '50-off-breach-discount' // to be used for most sales promotions
    | 'eofy'
    | 'business-pack'
    | 'sport-pack'
    | 'bali-bombing'
    | 'digital-only'

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscount50afl: boolean
    canShowBreachDiscount50sch: boolean
    canShowBreachDiscountBike: boolean
    canShowBreachDiscountGovan: boolean
    canShowBreachDiscountCovid: boolean
    canShowBlackFriday: boolean
    canShowBreachDiscountGeneric: boolean
    canShowEofyDiscount: boolean
    canShowBusinessPackDiscount: boolean
    canShowSportPackDiscount: boolean
    canShowBaliBombingDiscount: boolean
}

export function useBreachWallConfig(location: H.Location) {
    return getBreachWallContentType(location, {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscount50sch: useFeature('top-50-schools'),
        canShowBreachDiscountBike: useFeature(
            '30-off-breach-discount-biked130',
        ),
        canShowBreachDiscountGovan: useFeature(
            '50-off-breach-discount-govan50',
        ),
        canShowBreachDiscountCovid: useFeature(
            '50-off-breach-discount-coviddig',
        ),
        canShowBlackFriday: useFeature('black-friday'),
        canShowBreachDiscountGeneric: useFeature('50-off-breach-discount'),
        canShowEofyDiscount: useFeature('subs-campaign-eofy-weekend-digital'),
        canShowBusinessPackDiscount: useFeature(
            'subs-campaign-business-pack-2022',
        ),
        canShowSportPackDiscount: useFeature('subs-campaign-sport-pack-2022'),
        canShowBaliBombingDiscount: useFeature(
            'subs-campaign-bali-bombing-2022',
        ),
        canShowBreachDiscount50afl: useFeature('50-off-afl-breach-screen'),
    })
}

export function getBreachWallContentType(
    location: H.Location,
    options: BreachWallOptions,
): {
    deal: string
    creativeName: string
    type: BreachWallContentType
} {
    if (options.canShowBlackFriday) {
        return {
            creativeName: 'TW_Article_Breach_Black_Friday_1',
            deal: 'BLKFRI1',
            type: 'black-friday',
        }
    }

    if (showBreachDiscount(location, options.canShowBreachDiscountGovan)) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'GOVAN50',
            type: '50-off-breach-discount-govan50',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscountBike,
            '*',
            'bikie-code',
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BIKEDI30',
            type: '30-off-breach-discount-biked130',
        }
    }

    // Multiple AFL50 subtopic targets
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'afl',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'aflw',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'wafl',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'wafl-colts',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'psa-footy',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'west-coast-eagles',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }
    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50afl,
            '*',
            'fremantle-dockers',
            true,
            true
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'AFL50',
            type: '50-off-afl-breach-screen',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBreachDiscount50sch,
            '*',
            'top-50-schools',
            true,
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'SCHOOL30',
            type: 'top-50-schools',
        }
    }

    if (options.canShowBreachDiscountCovid) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'COVIDDIG',
            type: '50-off-breach-discount-coviddig',
        }
    }

    if (options.canShowBreachDiscountGeneric) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: BASE_50_DEAL_OFFERCODE,
            type: '50-off-breach-discount',
        }
    }

    if (options.canShowEofyDiscount) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'EOFY6',
            type: 'eofy',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBusinessPackDiscount,
            'business',
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BUSN2',
            type: 'business-pack',
        }
    }

    if (
        showBreachForTopic(location, options.canShowSportPackDiscount, 'sport')
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'SP3022',
            type: 'sport-pack',
        }
    }

    if (showBreachForTopic(location, options.canShowSportBreach, 'sport')) {
        return {
            creativeName: 'TW_Article_Breach_Article_SPORT_PACKAGE',
            deal: 'sportspack',
            type: 'sport',
        }
    }

    if (
        showBreachForTopic(
            location,
            options.canShowBaliBombingDiscount,
            '*',
            'bali-bombings',
            true,
        )
    ) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'BB2022',
            type: 'bali-bombing',
        }
    }

    return {
        creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
        deal: 'default',
        type: 'default',
    }
}

export const BreachNotificationWrapper: React.FC<BreachNotificationWrapperProps> =
    ({
        publication,
        breachDeal,
        breachCreativeName,
        requiredAccess,
        onEvent,
        type,
        contentOptions,
        blocks,
        log,
    }) => {
        const showLiveSportTrailer =
            contentOptions && contentOptions.showLiveSportHubTrailer

        useUpdateBreachWallContext({
            publication,
            breachCreativeName,
            breachDeal,
            requiredAccessLevel: requiredAccess.level,
        })

        const showPartOfArticle = useFeatureForSection({
            feature: 'tp-subscription-breach-show-part-article',
            sections: TheWestSections.default,
        })
        switch (type) {
            case 'sport':
                return (
                    <SportBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        onEvent={onEvent}
                    />
                )
            case 'black-friday':
                return (
                    <BlackFridayBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        onEvent={onEvent}
                    />
                )
            default:
                return (
                    <>
                        {showPartOfArticle && (
                            <ArticlePreview
                                blocks={blocks}
                                numberOfBlocksToPreview={2}
                                log={log}
                                onEvent={onEvent}
                            />
                        )}
                        <BreachNotification
                            breachDeal={breachDeal}
                            breachCreativeName={breachCreativeName}
                            onEvent={onEvent}
                            type={type}
                            contentOptions={{
                                showLiveSportTrailer,
                            }}
                        />
                    </>
                )
        }
    }

BreachNotificationWrapper.displayName = 'BreachNotificationWrapper'

export default BreachNotificationWrapper
